import React, { useState } from 'react';
import './FileForm.css';

const FileForm = ({ 
  label, 
  name, 
  onChange, 
  required, 
  accept,
  maxSize = 5, // tamaño máximo en MB
  className = ''
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const fileInputRef = React.useRef(null);
  const fileAreaRef = React.useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const newFiles = Array.from(e.dataTransfer.files);
    const validFiles = newFiles.filter(validateFile);
    
    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      setFiles(updatedFiles);
      onChange({ target: { name, value: updatedFiles[0], type: 'file' } });
      setError('');
    }
  };

  const validateFile = (file) => {
    if (file.size > maxSize * 1024 * 1024) {
      setError(`El archivo ${file.name} excede el tamaño máximo de ${maxSize}MB`);
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(validateFile);
    
    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      setFiles(updatedFiles);
      onChange({ target: { name, value: updatedFiles[0], type: 'file' } });
      setError('');
    }
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(updatedFiles);
    onChange({ 
      target: { 
        name, 
        value: updatedFiles.length > 0 ? updatedFiles[0] : null,
        type: 'file' 
      } 
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileAreaClick = (e) => {
    // Verificar que el clic ocurrió dentro del área de archivos
    if (e.target === fileAreaRef.current || fileAreaRef.current.contains(e.target)) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`form-group ${className}`}>
      <label htmlFor={name}>
        {label}
        {required && <span className="required">*</span>}
      </label>

      <div 
        className={`file-select-area ${dragActive ? 'drag-active' : ''}`}
        onClick={handleFileAreaClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        tabIndex="0"
        role="button"
        aria-controls={name}
        ref={fileAreaRef}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
      >
        <input
          ref={fileInputRef}
          id={name}
          type="file"
          name={name}
          onChange={handleChange}
          required={required && files.length === 0}
          accept={accept}
          multiple
          className="file-input"
          tabIndex="-1"
        />
        <span className="file-select-text">
          Seleccionar archivo o arrastrar aquí
        </span>
      </div>

      {files.length > 0 && (
        <div className="selected-files">
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className="file-item">
              <div className="file-info">
                <span className="file-icon">📄</span>
                <div className="file-details">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{formatFileSize(file.size)}</span>
                </div>
              </div>
              <button 
                type="button" 
                className="remove-file" 
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                aria-label="Eliminar archivo"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      
      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

export default FileForm; 